import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import DataGrid from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import * as consts from '../consts';

const SocialLink = ({ url, icon, title }) => {
    if (!url) return null;
    return (
        <a href={url} target="_blank" rel="noreferrer" title={title} className="me-2 text-primary" style={{ fontSize: '18px' }}>
            <i className={icon}></i>
        </a>
    );
};

const CompanyPageLink = ({ url, label, icon }) => {
    if (!url) return null;
    return (
        <a href={url} target="_blank" rel="noreferrer" className="badge me-1 mb-1 text-decoration-none" style={{ backgroundColor: '#e8f0fe', color: '#1a56db', padding: '4px 8px', borderRadius: '12px', fontSize: '11px' }}>
            <i className={`${icon} me-1`}></i>{label}
        </a>
    );
};

const StatCard = ({ value, label, icon }) => {
    if (!value && value !== 0) return null;
    return (
        <div className="d-flex flex-column align-items-center justify-content-center p-2 rounded" style={{ background: '#fff', border: '1px solid #e2e8f0', minWidth: '80px' }}>
            <i className={`${icon} text-primary`} style={{ fontSize: '16px' }}></i>
            <div className="fw-bold" style={{ fontSize: '14px' }}>{value}</div>
            <div className="text-muted" style={{ fontSize: '10px' }}>{label}</div>
        </div>
    );
};

const RatingStars = ({ rating, reviewCount }) => {
    if (!rating) return null;
    const stars = Math.round(rating);
    return (
        <span className="d-flex align-items-center gap-1">
            {[1,2,3,4,5].map(s => (
                <i key={s} className={`bi ${s <= stars ? 'bi-star-fill text-warning' : 'bi-star text-muted'}`} style={{ fontSize: '12px' }}></i>
            ))}
            <span className="text-muted ms-1" style={{ fontSize: '11px' }}>{rating.toFixed(1)}{reviewCount ? ` (${reviewCount.toLocaleString()})` : ''}</span>
        </span>
    );
};

function SingleLead(props) {
    const params = useParams();
    const db = props.db || params.db;
    const leadid = props.leadid || params.leadid;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lead, setLead] = useState(null);
    const [branches, setBranches] = useState([]);
    const [phones, setPhones] = useState([]);
    const [emails, setEmails] = useState([]);
    const [people, setPeople] = useState([]);

    useEffect(() => {
        setLoading(true);
        setError(null);
        axios.post('/getsinglelead', { leadid: parseInt(leadid), db })
            .then(res => {
                if (!res.data.lead) {
                    setError('Lead not found.');
                } else {
                    setLead(res.data.lead);
                    setBranches(res.data.branches || []);
                    setPhones(res.data.phones || []);
                    setEmails(res.data.emails.map(e => ({
                        ...e,
                        isprefixgeneric: e.isprefixgeneric ? 'Generic' : 'Specific',
                        issuffixgeneric: e.issuffixgeneric ? 'Generic' : 'Specific',
                    })) || []);
                    setPeople(res.data.people || []);
                }
                setLoading(false);
            })
            .catch(() => {
                setError('Failed to load lead details.');
                setLoading(false);
            });
    }, [leadid, db]);

    const branchCols = [
        { key: 'trimmedname', name: 'Name' },
        { key: 'fulladdress', name: 'Address' },
        { key: 'phone', name: 'Phone' },
        { key: 'marketpresence', name: 'Presence' },
        { key: 'marketreputation', name: 'Reputation' },
        { key: 'city', name: 'City' },
        { key: 'state', name: 'State' },
        { key: 'category_en', name: 'Category' },
    ];

    const peopleCols = [
        { key: 'name', name: 'Name' },
        { key: 'role', name: 'Role' },
        { key: 'email', name: 'Email' },
        { key: 'phone', name: 'Phone' },
        {
            key: 'linkedin',
            name: 'LinkedIn',
            formatter: ({ row }) => row.linkedin
                ? <a href={row.linkedin} target="_blank" rel="noreferrer"><i className="bi bi-linkedin text-primary"></i></a>
                : null
        },
    ];

    const phoneCols = [
        { key: 'phone', name: 'Phone' },
        { key: 'type', name: 'Type' },
        { key: 'iswhatsapp', name: 'WhatsApp', formatter: ({ row }) => row.iswhatsapp ? <i className="bi bi-check-circle-fill text-success"></i> : null },
        { key: 'ismobile', name: 'Mobile', formatter: ({ row }) => row.ismobile ? <i className="bi bi-check-circle-fill text-success"></i> : null },
    ];

    const emailCols = [
        { key: 'email', name: 'Email' },
        { key: 'isprefixgeneric', name: 'Prefix' },
        { key: 'issuffixgeneric', name: 'Suffix' },
    ];

    const defaultColOpts = { resizable: true };
    const sectionHeaderStyle = { background: '#0d6efd', color: '#fff', padding: '6px 10px', fontWeight: 600, fontSize: '12px', borderRadius: '2px 2px 0 0' };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px', background: 'var(--bg-wowleads)' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary mb-3" role="status"></div>
                    <div className="text-muted" style={{ fontSize: '14px' }}>Loading lead details...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px', background: 'var(--bg-wowleads)' }}>
                <div className="text-center">
                    <i className="bi bi-exclamation-circle text-danger" style={{ fontSize: '48px' }}></i>
                    <div className="mt-2 text-danger fw-semibold">{error}</div>
                </div>
            </div>
        );
    }

    if (!lead) return null;

    const bgImage = consts.getBgUrl(lead.ogimage, lead.logo, lead.gimgurl);
    const bgImageSize = consts.getBgSize(lead.ogimage, lead.logo, lead.gimgurl);
    const homeTitle = lead.hometitle || lead.ogtitle || '';
    const homeDescription = lead.homedescription || lead.ogdescription || '';
    const displayName = lead.trimmedname || lead.name || '';

    return (
        <div style={{ background: 'var(--bg-wowleads)', fontFamily: 'var(--font-family-sans-serif)', fontSize: 'small' }}>
            <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '16px' }}>

                {/* ── Header Card ── */}
                <div className="rounded shadow-sm mb-3 overflow-hidden" style={{ background: '#fff', border: '1px solid #e2e8f0' }}>
                    <div className="d-flex flex-row" style={{ minHeight: '140px' }}>
                        {/* Left: info */}
                        <div className="d-flex flex-column p-3 flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                <h5 className="mb-0 fw-bold text-primary">{displayName}</h5>
                                {lead.category_en && (
                                    <span className="badge" style={{ background: '#e8f0fe', color: '#1a56db', fontWeight: 500, fontSize: '10px' }}>
                                        {lead.category_en}
                                    </span>
                                )}
                            </div>

                            <RatingStars rating={lead.avgwrating || lead.rating} reviewCount={lead.reviewcount} />

                            {lead.fulladdress && (
                                <div className="mt-1 text-muted d-flex align-items-start gap-1" style={{ fontSize: '12px' }}>
                                    <i className="bi bi-geo-alt-fill text-primary mt-1" style={{ fontSize: '11px', flexShrink: 0 }}></i>
                                    <span>{lead.fulladdress}</span>
                                    {lead.gmapsuri && (
                                        <a href={lead.gmapsuri} target="_blank" rel="noreferrer" className="ms-1 text-primary" style={{ fontSize: '11px', flexShrink: 0 }}>
                                            Map
                                        </a>
                                    )}
                                </div>
                            )}

                            {lead.phone && (
                                <div className="mt-1 d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                                    <i className="bi bi-telephone-fill text-primary" style={{ fontSize: '11px' }}></i>
                                    <span>{lead.phone}</span>
                                </div>
                            )}

                            {lead.url && (
                                <div className="mt-1 d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                                    <i className="bi bi-globe text-primary" style={{ fontSize: '11px' }}></i>
                                    <a href={lead.url.startsWith('http') ? lead.url : `https://${lead.url}`} target="_blank" rel="noreferrer">{lead.tld || lead.url}</a>
                                </div>
                            )}

                            {/* Social links */}
                            <div className="mt-2 d-flex flex-wrap">
                                <SocialLink url={lead.facebookurl} icon="bi bi-facebook" title="Facebook" />
                                <SocialLink url={lead.twitterurl || lead.twitterurl_og} icon="bi bi-twitter-x" title="X / Twitter" />
                                <SocialLink url={lead.instagramurl || lead.instagramurl_og} icon="bi bi-instagram" title="Instagram" />
                                <SocialLink url={lead.linkedinurl || lead.linkedinurl_og} icon="bi bi-linkedin" title="LinkedIn" />
                                <SocialLink url={lead.youtubeurl || lead.youtubeurl_og} icon="bi bi-youtube" title="YouTube" />
                                <SocialLink url={lead.pinteresturl || lead.pinteresturl_og} icon="bi bi-pinterest" title="Pinterest" />
                                <SocialLink url={lead.whatsappurl} icon="bi bi-whatsapp" title="WhatsApp" />
                                <SocialLink url={lead.bookingurl} icon="bi bi-calendar-check" title="Booking" />
                            </div>

                            {/* Company pages */}
                            <div className="mt-2 d-flex flex-wrap">
                                <CompanyPageLink url={lead.contacturl} label="Contact" icon="bi bi-envelope" />
                                <CompanyPageLink url={lead.abouturl} label="About" icon="bi bi-info-circle" />
                                <CompanyPageLink url={lead.leadershipurl} label="Leadership" icon="bi bi-people" />
                                <CompanyPageLink url={lead.careersurl} label="Careers" icon="bi bi-briefcase" />
                                <CompanyPageLink url={lead.investorurl} label="Investor" icon="bi bi-graph-up" />
                                <CompanyPageLink url={lead.carturl} label="Cart" icon="bi bi-cart" />
                                <CompanyPageLink url={lead.producturl} label="Products" icon="bi bi-box-seam" />
                                <CompanyPageLink url={lead.mobileapps} label="Mobile App" icon="bi bi-phone" />
                            </div>
                        </div>

                        {/* Right: image */}
                        <div style={{ width: '180px', minWidth: '140px', flexShrink: 0 }}>
                            <div style={{
                                height: '100%',
                                minHeight: '140px',
                                backgroundImage: `url(${bgImage})`,
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                backgroundColor: '#b8cdfb',
                                backgroundSize: bgImageSize
                            }}></div>
                        </div>
                    </div>

                    {/* Title / Description bar */}
                    {(homeTitle || homeDescription) && (
                        <div className="border-top px-3 py-2" style={{ background: '#f8fafc' }}>
                            {homeTitle && <div className="fw-semibold" style={{ fontSize: '12px' }}>{homeTitle}</div>}
                            {homeDescription && <div className="text-muted" style={{ fontSize: '11px', marginTop: '2px' }}>{homeDescription}</div>}
                        </div>
                    )}
                </div>

                {/* ── Stats Row ── */}
                <div className="d-flex flex-wrap gap-2 mb-3">
                    <StatCard value={lead.branchcount} label="Branches" icon="bi bi-diagram-3" />
                    <StatCard value={lead.phonecount} label="Phones" icon="bi bi-telephone" />
                    <StatCard value={lead.emailcount} label="Emails" icon="bi bi-envelope" />
                    <StatCard value={lead.peoplecount} label="People" icon="bi bi-people" />
                    {lead.marketpresence && (
                        <div className="d-flex flex-column align-items-center justify-content-center p-2 rounded" style={{ background: '#fff', border: '1px solid #e2e8f0', minWidth: '80px' }}>
                            <i className="bi bi-signal text-primary" style={{ fontSize: '16px' }}></i>
                            <div className="fw-bold" style={{ fontSize: '13px' }}>{lead.marketpresence}</div>
                            <div className="text-muted" style={{ fontSize: '10px' }}>Presence</div>
                        </div>
                    )}
                    {lead.marketreputation && (
                        <div className="d-flex flex-column align-items-center justify-content-center p-2 rounded" style={{ background: '#fff', border: '1px solid #e2e8f0', minWidth: '80px' }}>
                            <i className="bi bi-shield-check text-primary" style={{ fontSize: '16px' }}></i>
                            <div className="fw-bold" style={{ fontSize: '13px' }}>{lead.marketreputation}</div>
                            <div className="text-muted" style={{ fontSize: '10px' }}>Reputation</div>
                        </div>
                    )}
                    {lead.totalreviews > 0 && <StatCard value={lead.totalreviews?.toLocaleString()} label="Reviews" icon="bi bi-star" />}
                    {lead.addresscount > 1 && <StatCard value={lead.addresscount} label="Addresses" icon="bi bi-pin-map" />}
                </div>

                {/* ── Branches ── */}
                <div className="mb-3 rounded overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                    <div style={sectionHeaderStyle}>Branches{branches.length > 0 ? ` (${branches.length})` : ''}</div>
                    <div style={{ background: '#fff' }}>
                        <DataGrid
                            columns={branchCols}
                            rows={branches}
                            rowHeight={28}
                            headerRowHeight={30}
                            className="rdg-light w-100"
                            style={{ fontSize: '12px', height: branches.length > 0 ? Math.min(branches.length * 28 + 45, 220) : 58 }}
                            defaultColumnOptions={defaultColOpts}
                        />
                    </div>
                </div>

                {/* ── People ── */}
                <div className="mb-3 rounded overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                    <div style={sectionHeaderStyle}>People{people.length > 0 ? ` (${people.length})` : ''}</div>
                    <div style={{ background: '#fff' }}>
                        <DataGrid
                            columns={peopleCols}
                            rows={people}
                            rowHeight={28}
                            headerRowHeight={30}
                            className="rdg-light w-100"
                            style={{ fontSize: '12px', height: people.length > 0 ? Math.min(people.length * 28 + 45, 220) : 58 }}
                            defaultColumnOptions={defaultColOpts}
                        />
                    </div>
                </div>

                {/* ── Phone Numbers ── */}
                <div className="mb-3 rounded overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                    <div style={sectionHeaderStyle}>Phone Numbers{phones.length > 0 ? ` (${phones.length})` : ''}</div>
                    <div style={{ background: '#fff' }}>
                        <DataGrid
                            columns={phoneCols}
                            rows={phones}
                            rowHeight={28}
                            headerRowHeight={30}
                            className="rdg-light w-100"
                            style={{ fontSize: '12px', height: phones.length > 0 ? Math.min(phones.length * 28 + 45, 200) : 58 }}
                            defaultColumnOptions={defaultColOpts}
                        />
                    </div>
                </div>

                {/* ── Email IDs ── */}
                <div className="mb-3 rounded overflow-hidden shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
                    <div style={sectionHeaderStyle}>Email IDs{emails.length > 0 ? ` (${emails.length})` : ''}</div>
                    <div style={{ background: '#fff' }}>
                        <DataGrid
                            columns={emailCols}
                            rows={emails}
                            rowHeight={28}
                            headerRowHeight={30}
                            className="rdg-light w-100"
                            style={{ fontSize: '12px', height: emails.length > 0 ? Math.min(emails.length * 28 + 45, 200) : 58 }}
                            defaultColumnOptions={defaultColOpts}
                        />
                    </div>
                </div>

            </div>
        </div>
    );
}

export default SingleLead;
